<script setup>
    /**
     * Vue Scripts
     */
    import {onMounted, reactive, ref, toRefs} from "vue";
    import {getQueryParams} from "@util/api";
    import {snakeCaseConvert} from "@util/util";
    import intlTelInput from 'intl-tel-input';

    /**
     * Vue Styles
     */
    import 'intl-tel-input/build/css/intlTelInput.css';

    const {
        api,
        formLabels,
        images,
        studyKikId,
        taOverride,
    } = STUDYKIK

    console.log(formLabels)

    const {poweredBy, termsAndConditions, title} = formLabels;
    const {logo, spinner,} = images;
    const resetLabel = formLabels.reset
    const submitLabel = formLabels.submit

    const emit = defineEmits(['signupCompleted'])

    const formName = {
        label: formLabels.name,
        errMessage: formLabels.nameErrorMessage,
        placeholder: formLabels.namePlaceholder
    }

    const formEmail = {
        label: formLabels.email,
        errMessage: formLabels.emailErrorMessage,
        placeholder: formLabels.emailPlaceholder
    }

    const formPhone = {
        label: formLabels.phone,
        errMessage: formLabels.phoneErrorMessage,
        placeholder: formLabels.phonePlaceholder
    }

    const formPostalCode = {
        label: formLabels.postalCode,
        errMessage: formLabels.postalCodeErrorMessage,
        placeholder: formLabels.postalCodePlaceholder
    }

    const initialFormState = {
        fullName: '',
        postalCode: '',
        email: '',
        phone: '',
        country: 'us',
        isSubmitting: false,
        serverError: '',
        utm: {},
    };

    const initialErrorState = {
        emailErr: false,
        fullNameErr: false,
        phoneErr: false,
        postalCodeErr: false,
    }

    const formState = reactive({...initialFormState})
    const validationErrorsState = reactive({...initialErrorState})

    const {
        fullName,
        postalCode,
        email,
        phone,
        country,
        isSubmitting,
        serverError,
        utm,
    } = toRefs(formState);

    const {
        emailErr,
        fullNameErr,
        phoneErr,
        postalCodeErr,
    } = toRefs(validationErrorsState)
    const iniPhone = ref()

    onMounted(() => {
        buildUtm()
        instantiatePhone()
    })

    //region onMounted Methods
    /**
     * Grabs the UTM query from the URL
     * and assigns it to the utm ref
     */
    const buildUtm = () => {
        const url = new URL(window.location);
        const searchParams = getQueryParams(url?.searchParams || [])

        Object.entries(searchParams)
            .forEach(entry => {
                const key = entry[0] || '';
                key.includes('utm') && (utm.value[snakeCaseConvert(key)] = entry[1] || '')
                return '';
            })
    }

    /**
     * Sets up the intlTelInput phone plugin
     */
    const instantiatePhone = () => {
        const phoneInput = document.getElementById('phone')

        intlTelInput(phoneInput, {
            utilsScript:
                "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.min.js",
        });

        iniPhone.value = window.intlTelInputGlobals.getInstance(phoneInput)
        phoneInput.addEventListener('countrychange', () => {
            const countryName = iniPhone.value.getSelectedCountryData().iso2

            !validatePhone() && (validationErrorsState.phoneErr = true)

            country.value = countryName;
        })
    }
    //endregion

    //region Form Actions
    /**
     * Handles the form submission
     * @returns {Promise<void>}
     */
    const onSubmit = async () => {
        // Clear any form errors that persist
        clearErrors()

        if (!validateFields()) return

        console.log('form validated')

        const payload = getFormattedPayload();

        isSubmitting.value = true;

        try {
            const response = await fetch(
                api,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            )

            const responseData = await response.json()

            if (response.ok) {
                emit('signupCompleted', responseData)
            } else {
                handleRequestError(responseData)
            }

        } catch (error) {
            console.error("Unable to signup patient: Err: ", error);
        } finally {
            isSubmitting.value = false;
        }
    }

    /**
     * Reset the form values and any error states
     */
    const resetForm = () => {
        Object.assign(formState, initialFormState)
        resetValidationErr()
    }
    //endregion

    //region Form Methods
    /**
     * Reset the form error states
     * @returns {UnwrapNestedRefs<{emailErr: boolean, fullNameErr: boolean, phoneErr: boolean, postalCodeErr: boolean}> & {} & {emailErr: boolean, fullNameErr: boolean, phoneErr: boolean, postalCodeErr: boolean}}
     */
    const resetValidationErr = () => Object.assign(validationErrorsState, initialErrorState)

    /**
     * Clears any form errors after validation
     */
    const clearErrors = () => {
        resetValidationErr()
        serverError.value = ''
    }

    /**
     * Fetches all the data needed to deliver via an API call
     *
     * @returns {*&{protocolLandingPageUUID: *, firstName: *, lastName: *, email: *, phone: *, postalCode: *, timezone: null, country: string}}
     */
    const getFormattedPayload = () => {
        let timezone = null;
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (error) {
            console.error("Unable to get user timezone", error);
        }

        const [firstName, ...lastNames] = fullName.value.split(" ");

        const payload = {
            protocolLandingPageUUID: studyKikId,
            firstName,
            lastName: lastNames.join(" "),
            email: email.value,
            phone: phone.value,
            postalCode: postalCode.value,
            timezone,
            country: country.value.toUpperCase(),
            ...utm.value,
        }

        payload['typeform-welcome'] = 0;

        return payload;
    }

    /**
     * Handles any server codes from StudyKik
     *
     * @param {Object} responseData The API responses data
     */
    const handleRequestError = responseData => {
        if (responseData.errorCode === 'invalidPostalCode') {
            validationErrorsState.postalCodeErr = true;
        }

        const serverErrorCodes = [
            "dailyLimitReached",
            "patientPhoneAlreadyExists",
            "invalidPostalCode",
            "patientAlreadyExistsOnProtocolPage",
        ];

        serverError.value = (
            serverErrorCodes.some((code) => code === responseData.errorCode)
                && responseData.message) || "Something went wrong. Please try again."
    }

    /**
     * Validated the phone input field
     *
     * @returns {*|null}
     */
    const validatePhone = () => iniPhone.value.isValidNumber()

    /**
     * Validates all the fields to ensure that they all had data in them
     *
     * @returns {*}
     */
    const validateFields = () => {
        resetValidationErr()
        const errObj = {}

        if (fullName.value.trim() === '') {
            validationErrorsState.fullNameErr = true;
            errObj.fullName = true;
        }

        if (!/^[\w-\.+]+@([\w-]+\.)+[\w-]{2,4}$/.test(email.value)) {
            validationErrorsState.emailErr = true;
            errObj.email = true;
        }

        if (!validatePhone()) {
            validationErrorsState.phoneErr = true;
            errObj.phone = true;
        }

        if (postalCode.value.trim() === "") {
            validationErrorsState.postalCodeErr = true;
            errObj.postalCode = true;
        }

        return Object.keys(errObj).length === 0
    }
    //endregion

</script>

<template>
    <form
        class="signup-form"
        @submit.prevent="onSubmit"
    >

        <div class="title-container">
            <h3
                class="title"
                v-html="title"
            />
        </div>

        <div
            v-if="serverError"
            class="server-error"
        >
            <p
                class="message"
                v-html="serverError"
            />
        </div>

        <div class="input-container">
            <label
                class="input-label screen-reader-text"
                for="full-name"
                v-html="formName.label"
            />
            <input
                class="input-field"
                :class="{ error: fullNameErr }"
                id="full-name"
                ref="focusName"
                :placeholder="formName.placeholder"
                :aria-placeholder="formName.placeholder"
                v-model="fullName"
            >
            <p
                v-show="fullNameErr"
                class="input-error"
                v-html="formName.errMessage"
            />
        </div>

        <div class="input-container">
            <label
                class="input-label screen-reader-text"
                for="email"
                v-html="formEmail.label"
            />
            <input
                class="input-field"
                :class="{ error: emailErr }"
                id="email"
                :placeholder="formEmail.placeholder"
                :aria-placeholder="formEmail.placeholder"
                v-model="email"
            >
            <p
                v-show="emailErr"
                class="input-error"
                v-html="formEmail.errMessage"
            />
        </div>

        <div class="input-container">
            <label
                class="input-label screen-reader-text"
                for="phone"
                v-html="formPhone.label"
            />
            <input
                class="input-phone-field"
                :class="{ error: phoneErr }"
                id="phone"
                :placeholder="formPhone.placeholder"
                :aria-placeholder="formPhone.placeholder"
                v-model="phone"
            >
            <p
                v-show="phoneErr"
                class="input-error"
                v-html="formPhone.errMessage"
            />
        </div>

        <div class="input-container">
            <label
                class="input-label screen-reader-text"
                for="postal-code"
                v-html="formPostalCode.label"
            />
            <input
                class="input-field"
                :class="{ error: postalCodeErr }"
                id="postal-code"
                :placeholder="formPostalCode.placeholder"
                :aria-placeholder="formPostalCode.placeholder"
                v-model="postalCode"
            >
            <p
                v-show="postalCodeErr"
                class="input-error"
                v-html="formPostalCode.errMessage"
            />
        </div>

        <div
            class="button-container"
            tabindex="-1"
        >
            <button
                class="submit-button"
                type="submit"
                :disabled="isSubmitting"
            >
                {{ submitLabel }}
                <span
                    v-if="isSubmitting"
                    id="sign-up-submit-spinner"
                    class="spinner-container"
                >
                        <img
                            class="spinner-icon"
                            :src="spinner"
                            alt="Loading Spinner"
                        />
                    </span>
            </button>

            <button
                class="reset-button"
                type="button"
                @click="resetForm"
                @keydown.prevent.enter="resetForm"
                v-html="resetLabel"
            />
        </div>

        <p
            class="terms-and-conditions"
            v-html="taOverride || termsAndConditions"
        />

        <div class="powered-by-container">
            <p
                class="title"
                v-html="poweredBy"/>
            <img
                class="studykik-logo"
                :src="logo"
                alt="StudyKik Logo"
            />
        </div>

    </form>
</template>

<script>
    export default {
        name: "StudyKik SignUp Form"
    }
</script>

<style>
    /* The following below is for the telephone input field
   ===================================== */
    .iti__flag {
        background-image: url("https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/img/flags.png");
    }

    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
        .iti__flag {
            background-image: url("https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/img/flags@2x.png");
        }
    }
</style>

<style scoped lang="scss">
    .checkbox-container {
        display: flex;
        align-content: flex-start;
        justify-content: flex-start;

        label {
            &::before {
                display: none;
            }
        }

        input[type="checkbox"] {
            flex: 0 0 20px;
            max-width: 20px;
            position: relative;
            top: auto;
            left: auto;
            height: auto !important;
            opacity: 1;
        }

        label {
            font-size: 0.85rem;
            flex: 1 0 auto;
            width: auto;
            max-width: 100%;
            margin-bottom: 0;
            padding-left: 10px;
            cursor: text;
        }

        p {
            flex: 0 0 100%;
            width: 100%;
        }
    }
</style>
